import MobileDetect from 'mobile-detect'
import { ref, watch } from '@vue/runtime-core'
import { screens } from 'windi-theme'
import { useBreakpoints, useDebounceFn } from '@vueuse/core'

/**
 * @typedef {Object} Platform
 * @property {import('node_modules/@vue/reactivity/dist/reactivity').Ref<boolean>} mobile
 * @property {import('node_modules/@vue/reactivity/dist/reactivity').Ref<boolean>} phone
 * @property {import('node_modules/@vue/reactivity/dist/reactivity').Ref<boolean>} tablet
 * @property {import('node_modules/@vue/reactivity/dist/reactivity').Ref<boolean>} android
 * @property {import('node_modules/@vue/reactivity/dist/reactivity').Ref<boolean>} ios
 * @property {import('node_modules/@vue/reactivity/dist/reactivity').Ref<boolean>} ipad
 * @property {import('node_modules/@vue/reactivity/dist/reactivity').Ref<boolean>} iphone
 * @property {import('node_modules/@vue/reactivity/dist/reactivity').Ref<boolean>} edge
 * @property {import('node_modules/@vue/reactivity/dist/reactivity').Ref<boolean>} firefox
 * @property {import('node_modules/@vue/reactivity/dist/reactivity').Ref<boolean>} ie11
 * @property {import('node_modules/@vue/reactivity/dist/reactivity').Ref<boolean>} safari
 * @property {import('node_modules/@vue/reactivity/dist/reactivity').Ref<boolean>} prerenderer
 * @property {import('node_modules/@vue/reactivity/dist/reactivity').Ref<boolean>} desktop
 */

/**
 * @type Platform
 */
const result = {
  mobile: ref(null),
  phone: ref(null),
  tablet: ref(null),
  android: ref(null),
  ios: ref(null),
  ipad: ref(null),
  iphone: ref(null),
  edge: ref(null),
  firefox: ref(null),
  ie11: ref(null),
  safari: ref(null),
  prerenderer: ref(null),
  desktop: ref(null)
}
/**
 * Returns platform information, recalculated at each breakpoint change
 *
 * @returns {Platform} Object containing the current platform information
 */
export const usePlatform = () => {
  const { lg, md, sm, xl } = useBreakpoints(screens)

  const computeResult = useDebounceFn(() => {
    // console.log('compute platform')
    const userAgent = window.navigator.userAgent
    const md = new MobileDetect(userAgent)

    const isNewIpad = 'maxTouchPoints' in window.navigator &&
      userAgent.match(/Mac/) &&
      window.navigator.maxTouchPoints && // eslint-disable-line compat/compat
      window.navigator.maxTouchPoints > 2 // eslint-disable-line compat/compat

    result.mobile.value = !!md.mobile() || isNewIpad
    result.phone.value = !!md.phone()
    result.tablet.value = !!md.tablet() || isNewIpad
    result.android.value = !!md.is('AndroidOS')
    result.ios.value = !!md.is('iOS')
    result.ipad.value = !!md.is('iPad') || isNewIpad
    result.iphone.value = !!md.is('iPhone')
    result.edge.value = !!/Edge\/\d+/i.test(userAgent)
    result.firefox.value = md.version('Gecko') > 1
    result.ie11.value = !!/Trident.*rv:11\./i.test(userAgent)
    result.safari.value = /Safari/.test(userAgent) && /Apple Computer/.test(window.navigator.vendor)
    result.prerenderer.value = window.__PRERENDER_INJECTED !== undefined // /PhantomJS/.test(userAgent),
    result.desktop.value = !result.mobile.value && !result.tablet.value
  }, 100)

  watch([lg, md, sm, xl], computeResult)
  computeResult()

  return result
}

export default usePlatform
